import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { Vehicle } from "@autorepair/contracts";
import { customersApi, vehiclesApi, workOrdersApi, tenantSettingsApi } from "../lib/api";
import { firstLine, formatDate, formatMoney, formatWorkOrderNumber } from "../lib/money";
import { useShopType } from "../lib/theme";
import { VehicleDetailsModal } from "../components/VehicleDetailsModal";
import { Spinner } from "../components/ui-modern";
import {
  AvatarBadge,
  Eyebrow,
  ModernBreadcrumbs,
  ModernButton,
  ModernTintButton,
  ModernCard,
  ModernConfirmButton,
  ModernEmptyState,
  ModernErrorBanner,
  ModernStatusPill,
} from "../components/ui-modern";

/**
 * The modern theme's Customer Detail page — see docs/FRONTEND_BACKLOG.md
 * Round 59/60 and `Bay2Bill Redesign.dc.html` lines ~576-650 (the
 * "isCustomer" block).
 *
 * All data-fetching and mutations below are a deliberate line-for-line
 * duplicate of CustomerDetailPageClassic.tsx (same queryKeys/queryFns/
 * mutationFns, same enabled/derived logic) rather than a shared hook, per
 * this round's own instructions — keeps the two theme files independently
 * readable at the cost of some duplication, same tradeoff DashboardPage
 * made in Round 59.
 *
 * IMPORTANT — one honest substitution vs. the mockup's literal demo data,
 * same "restyle what's real, don't fabricate" rule Round 59's Dashboard
 * used: Service history's stage pill. The mockup shows exactly ONE pill per
 * row. The real work order list here carries TWO independent status axes
 * (job progress `wo.status`, and separate billing/invoice status
 * `wo.invoiceStatus`) — Classic shows both in two labeled columns. This
 * keeps BOTH as two small pills side by side rather than dropping the
 * billing status, which would be a real information loss, not just a
 * visual simplification.
 *
 * The "Account" card's Lifetime spend/Open balance WERE a similar
 * substitution (Vehicles/Visits/Customer since only) until the backend
 * grew a real per-customer invoice-financials aggregate
 * (`CustomersService.getFinancials`, `GET /customers/:id` and
 * `/customers/by-number/:n` now both return `CustomerDetail`'s
 * `lifetimeSpend`/`openBalance`) — see docs/FRONTEND_BACKLOG.md. Vehicles
 * count is kept alongside them (not in the mockup, but real, existing
 * information this page already had — no reason to drop it).
 */
export function CustomerDetailPageModern() {
  const hasVehicles = useShopType() !== "detailing";
  const { customerNumber: customerNumberParam } = useParams();
  const customerNumber = Number(customerNumberParam);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const requestedVehicleId = searchParams.get("vehicleId") ? Number(searchParams.get("vehicleId")) : null;
  const [detailsVehicle, setDetailsVehicle] = useState<Vehicle | null>(null);
  // Gates the vehicle-image thumbnails below — off by default per-shop, see
  // tenant_settings.vehicleImageEnabled's own doc comment.
  const tenantSettingsQuery = useQuery({ queryKey: ["tenant-settings"], queryFn: tenantSettingsApi.get });
  const vehicleImageEnabled = tenantSettingsQuery.data?.settings.vehicleImageEnabled ?? false;
  const [notice, setNotice] = useState<string | null>(null);
  // undefined = no manual choice yet (fall back to ?vehicleId= from a search
  // result, once we know that vehicle actually has work orders); null = the
  // user explicitly chose "Show All Work Orders".
  const [manualFilterVehicleId, setManualFilterVehicleId] = useState<number | null | undefined>(undefined);

  const customerQuery = useQuery({
    queryKey: ["customer", "by-number", customerNumber],
    queryFn: () => customersApi.getByNumber(customerNumber),
  });
  const id = customerQuery.data?.customer.id as number;
  const vehiclesQuery = useQuery({
    queryKey: ["vehicles", "byCustomer", id],
    queryFn: () => vehiclesApi.list({ customerId: id, limit: 50 }),
    enabled: !!customerQuery.data,
  });
  const workOrdersQuery = useQuery({
    queryKey: ["work-orders", "byCustomer", id],
    queryFn: () => workOrdersApi.list({ customerId: id, includeVoid: true, limit: 50 }),
    enabled: !!customerQuery.data,
  });

  const requestedVehicleHasWorkOrders =
    !!requestedVehicleId && !!workOrdersQuery.data?.items.some((wo) => wo.vehicleId === requestedVehicleId);
  const filterVehicleId =
    manualFilterVehicleId !== undefined ? manualFilterVehicleId : requestedVehicleHasWorkOrders ? requestedVehicleId : null;
  const setFilterVehicleId = setManualFilterVehicleId;

  // A vehicle can only be deleted once no ACTIVE (non-void) work order
  // still references it — mirrors VehiclesService.delete()'s own
  // server-side check. A work order can never reach "void" while it still
  // has a live invoice against it (issuing one moves it to "invoiced", and
  // that can't be voided — see WorkOrdersService's own canVoid rule), so
  // checking work-order status alone also correctly rules out an active
  // invoice, with no separate invoice query needed. Any VOID work order
  // (and its invoice, if any) is deleted right along with the vehicle —
  // by explicit owner decision, that history isn't kept once the vehicle
  // itself is gone.
  const deleteVehicleMutation = useMutation({
    mutationFn: (vehicleId: number) => vehiclesApi.delete(vehicleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicles", "byCustomer", id] });
      // Deleting a vehicle also purges any VOID work order (and its
      // invoice, if any) still referencing it — see this mutation's own
      // doc comment above. Without this, the Service History card below
      // (and canDeleteCustomer, which depends on this same query) kept
      // showing the now-deleted work order until a manual refresh — same
      // class of bug as the invoice detail page's stale query key.
      queryClient.invalidateQueries({ queryKey: ["work-orders", "byCustomer", id] });
    },
    onError: (err: unknown) => setNotice(err instanceof Error ? err.message : "Something went wrong. Please try again."),
  });

  // Mirrors the backend's own gate (CustomersService.delete()): blocked
  // while ANY vehicle or work order references this customer, active or
  // not — unlike vehicle deletion, there's no "void history is purged
  // instead of blocking" exception here. `undefined` (still loading)
  // counts as "not deletable" so the button never flashes on then off.
  //
  // The vehicle check is skipped for a `detailing` tenant (same
  // `hasVehicles` flag above) — their "vehicle" is always the invisible
  // per-customer placeholder auto-provisioned by WorkOrdersService, never
  // something the owner created or can see. Without this, the button
  // vanished forever the moment a customer's first work order was ever
  // created (which is exactly when that placeholder appears), since
  // vehiclesQuery would never again report zero items even after every
  // work order was later deleted.
  const canDeleteCustomer =
    (!hasVehicles || vehiclesQuery.data?.items.length === 0) && workOrdersQuery.data?.items.length === 0;
  const deleteCustomerMutation = useMutation({
    mutationFn: () => customersApi.delete(id),
    onSuccess: () => {
      // Without this, CustomersListPage's cached ["customers", search] query
      // kept showing the just-deleted customer until a manual refresh —
      // same class of bug as Round 12's invoice-list staleness fix.
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      navigate("/customers");
    },
    onError: (err: unknown) => setNotice(err instanceof Error ? err.message : "Something went wrong. Please try again."),
  });

  if (customerQuery.isLoading) return <Spinner label="Loading customer…" />;
  if (customerQuery.error || !customerQuery.data) return <ModernErrorBanner message="Could not find that customer." />;

  const { customer } = customerQuery.data;
  const initials =
    customer.displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join("") || "?";
  const primaryPhone = customer.phones.find((p) => p.isPrimary) ?? customer.phones[0];
  const contactLine = [primaryPhone?.phoneNumber, customer.email].filter(Boolean).join(" · ");

  const filteredVehicle = filterVehicleId ? vehiclesQuery.data?.items.find((v) => v.id === filterVehicleId) : undefined;
  const visibleWorkOrders = filterVehicleId
    ? workOrdersQuery.data?.items.filter((wo) => wo.vehicleId === filterVehicleId)
    : workOrdersQuery.data?.items;

  return (
    <div>
      <ModernBreadcrumbs items={[{ label: "Customers", to: "/customers" }, { label: customer.displayName }]} />

      <div className="mb-5 flex items-center gap-3.5">
        <div className="hidden lg:block">
          <AvatarBadge initials={initials} />
        </div>
        <div className="min-w-0 flex-1">
          <Eyebrow className="mb-2 text-[11px] text-[#0f6d86]">Customer #{customer.customerNumber}</Eyebrow>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <div className="order-1 lg:hidden">
                  <AvatarBadge initials={initials} />
                </div>
                <h1 className="order-2 m-0 text-[27px] font-bold text-[#071a33]">{customer.displayName}</h1>
                <p className="order-3 -mt-3 w-full pl-16 text-[13.5px] lg:pl-0 sm:-mt-2 text-[#4a5361] sm:order-4">
                  {contactLine || "No contact info on file."}
                </p>
                <Link to={`/customers/${customer.customerNumber}/edit`} className="order-4 sm:order-3">
                  <ModernTintButton>Edit this customer</ModernTintButton>
                </Link>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* No ?vehicleId= preset — NewWorkOrderPage already handles a bare
                  ?customerId= by prompting "Which Vehicle?" first, so this stays
                  a real, working "start a job for this customer" action even
                  with several vehicles on file (the mockup's header button
                  doesn't scope to one vehicle either). */}
              <Link to={`/work-orders/new?customerId=${id}`}>
                <ModernButton variant="primary">Start work order</ModernButton>
              </Link>
              {/* Not in the mockup's header (which only shows Edit + Start work
                  order), but dropping this would remove a real, working action —
                  same gate (ConfirmButton) as CustomerDetailPageClassic.tsx. */}
              {canDeleteCustomer && (
                <ModernConfirmButton
                  variant="danger"
                  confirmText="Permanently delete this customer? This can't be undone."
                  disabled={deleteCustomerMutation.isPending}
                  onConfirm={() => deleteCustomerMutation.mutate()}
                >
                  Delete
                </ModernConfirmButton>
              )}
            </div>
          </div>
        </div>
      </div>

      {notice && <ModernErrorBanner message={notice} />}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1.65fr)_minmax(280px,.85fr)]">
        <ModernCard className="min-w-0 p-0">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#eef1f6] px-[18px] py-[15px]">
            <h2 className="m-0 text-[15px] font-bold text-[#071a33]">
              Service history
              {filteredVehicle &&
                ` — ${[filteredVehicle.modelYear, filteredVehicle.make, filteredVehicle.model, filteredVehicle.trim]
                  .filter(Boolean)
                  .join(" ")}`}
            </h2>
            {filterVehicleId && (
              <ModernButton variant="ghost" className="min-h-0 h-8 px-3 text-[12.5px]" onClick={() => setFilterVehicleId(null)}>
                Show All Work Orders
              </ModernButton>
            )}
          </div>
          {workOrdersQuery.isLoading && (
            <div className="p-[18px]">
              <Spinner />
            </div>
          )}
          {visibleWorkOrders?.length === 0 && (
            <div className="p-[18px]">
              <ModernEmptyState>{filterVehicleId ? "No work orders yet for this vehicle." : "No work orders yet."}</ModernEmptyState>
            </div>
          )}
          {visibleWorkOrders?.map((wo) => (
            <Link
              key={wo.id}
              to={`/work-orders/${wo.workOrderNumber}`}
              state={{ from: `/customers/${customer.customerNumber}` }}
              className="flex flex-wrap items-center justify-between gap-3 border-b border-[#f1f4f9] px-[18px] py-3.5 no-underline last:border-b-0 hover:bg-[#f6f8fb]"
            >
              <span className="min-w-0">
                <span className="block text-[14px] font-semibold text-[#071a33]">
                  {wo.customerConcern || `Work Order ${formatWorkOrderNumber(wo.workOrderNumber)}`}
                </span>
                <span
                  className="mt-[3px] block text-[11.5px] text-[#8b97a8]"
                  style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace" }}
                >
                  {formatWorkOrderNumber(wo.workOrderNumber)} · {formatDate(wo.openedAt)}
                  {wo.vehicleDescription ? ` · ${firstLine(wo.vehicleDescription)}` : ""}
                  {wo.technicianNames ? ` · ${wo.technicianNames}` : ""}
                </span>
              </span>
              <span className="flex flex-none items-center gap-2">
                {/* Same job-progress/billing split as Classic's two labeled
                    columns — see this file's own header comment. */}
                <ModernStatusPill status={wo.status === "invoiced" ? "complete" : wo.status} />
                <ModernStatusPill status={wo.invoiceStatus ?? "not_invoiced"} />
                <span className="text-[14px] font-bold tabular-nums text-[#071a33]">{formatMoney(wo.total)}</span>
              </span>
            </Link>
          ))}
        </ModernCard>

        <aside className="flex min-w-0 flex-col gap-3.5">
          {hasVehicles && (
          <ModernCard>
            <div className="mb-[13px] flex items-center justify-between gap-2.5">
              <h2 className="m-0 text-[14px] font-bold text-[#071a33]">Vehicles</h2>
              <Link to={`/customers/${customer.customerNumber}/vehicles/new`}>
                <ModernTintButton>+ Add Vehicle for Customer</ModernTintButton>
              </Link>
            </div>
            {vehiclesQuery.isLoading && <Spinner />}
            {vehiclesQuery.data?.items.length === 0 && <ModernEmptyState>No vehicles yet for this customer.</ModernEmptyState>}
            <div className="flex flex-col gap-2.5">
              {vehiclesQuery.data?.items.map((v) => {
                // Only known-safe once the work-order history has loaded —
                // defaults to "not deletable" while loading rather than
                // flashing the button on and then hiding it.
                const canDelete =
                  !!workOrdersQuery.data && !workOrdersQuery.data.items.some((wo) => wo.vehicleId === v.id && wo.status !== "void");
                const hasWorkOrders = !!workOrdersQuery.data?.items.some((wo) => wo.vehicleId === v.id);
                const title = [v.modelYear, v.make, v.model].filter(Boolean).join(" ") || "Vehicle with no details yet";
                const metaLine = [
                  v.trim,
                  v.licensePlate ? `Plate ${v.licensePlate}` : null,
                  v.lastOdometer != null ? `${v.lastOdometer.toLocaleString()} mi` : null,
                ]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <div key={v.id} className="rounded-[10px] border border-[#e8ecf3] p-[13px]">
                    <div className="flex gap-3">
                      {vehicleImageEnabled && v.imageUrl && (
                        // Just a pasted URL, not an uploaded/validated asset
                        // — a dead link is expected occasionally, so hide
                        // the broken-image box rather than showing one.
                        <img
                          src={v.imageUrl}
                          alt=""
                          className="h-14 w-20 flex-none rounded-lg border border-[#e2e7ee] object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] font-semibold text-[#071a33]">{title}</div>
                        {metaLine && <div className="mt-[3px] text-[12.5px] text-[#6b7a8f]">{metaLine}</div>}
                        {!v.licensePlate && !v.trim && v.lastOdometer == null && (
                          <div className="mt-[3px] text-[12.5px] text-[#6b7a8f]">No details on file</div>
                        )}
                      </div>
                      <div className="flex flex-none flex-col gap-1.5">
                        <ModernButton
                          variant="secondary"
                          className="h-8 min-h-0 px-3 text-[12.5px]"
                          onClick={() => setDetailsVehicle(v)}
                        >
                          Vehicle Details
                        </ModernButton>
                        <Link to={`/customers/${customer.customerNumber}/vehicles/${v.id}/edit`} className="flex">
                          <ModernTintButton className="w-full">Edit Vehicle</ModernTintButton>
                        </Link>
                      </div>
                    </div>
                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                      <Link to={`/work-orders/new?customerId=${id}&vehicleId=${v.id}`}>
                        <ModernButton variant="primary" className="h-[34px] min-h-0 px-[11px] text-[12.5px]">
                          Start work order
                        </ModernButton>
                      </Link>
                      {hasWorkOrders && (
                        <ModernButton
                          variant="secondary"
                          className="h-[34px] min-h-0 px-[11px] text-[12.5px]"
                          onClick={() => setFilterVehicleId(v.id)}
                        >
                          Show Work Orders for This Vehicle
                        </ModernButton>
                      )}
                      {canDelete && (
                        <ModernConfirmButton
                          variant="danger"
                          className="h-[34px] min-h-0 px-[11px] text-[12.5px]"
                          confirmText="Permanently delete this vehicle, along with any of its voided work orders and invoices?"
                          disabled={deleteVehicleMutation.isPending}
                          onConfirm={() => deleteVehicleMutation.mutate(v.id)}
                        >
                          Delete
                        </ModernConfirmButton>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ModernCard>
          )}

          <ModernCard>
            <h2 className="m-0 mb-3 text-[14px] font-bold text-[#071a33]">Account</h2>
            <dl className="m-0 grid grid-cols-[1fr_auto] gap-y-2.5 text-[13.5px]">
              <dt className="text-[#6b7a8f]">Lifetime spend</dt>
              <dd className="m-0 text-right font-semibold tabular-nums text-[#071a33]">{formatMoney(customer.lifetimeSpend)}</dd>
              <dt className="text-[#6b7a8f]">Open balance</dt>
              <dd
                className={`m-0 text-right tabular-nums ${
                  customer.openBalance !== "0.00" ? "font-bold text-[#9a6700]" : "font-semibold text-[#071a33]"
                }`}
              >
                {formatMoney(customer.openBalance)}
              </dd>
              {hasVehicles && (
                <>
                  <dt className="text-[#6b7a8f]">Vehicles</dt>
                  <dd className="m-0 text-right font-semibold tabular-nums text-[#071a33]">{vehiclesQuery.data?.items.length ?? "—"}</dd>
                </>
              )}
              <dt className="text-[#6b7a8f]">Visits</dt>
              <dd className="m-0 text-right font-semibold tabular-nums text-[#071a33]">{workOrdersQuery.data?.items.length ?? "—"}</dd>
              <dt className="text-[#6b7a8f]">Customer since</dt>
              <dd className="m-0 text-right font-semibold text-[#071a33]">{customer.createdAt.slice(0, 4)}</dd>
            </dl>
          </ModernCard>
        </aside>
      </div>

      {detailsVehicle && <VehicleDetailsModal vehicle={detailsVehicle} onClose={() => setDetailsVehicle(null)} />}
    </div>
  );
}
